import { createSlice } from '@reduxjs/toolkit'
import { v4 } from 'uuid'
import { apiRequestPerformanceObserver } from '#features/DebugSettings/RequestMonitor/ApiRequestPerformanceObserver'
import { isStoredStateOfType, type ReduxVersion, type StoredState } from '#store/middleware/localStorage'

export type PendingApiRequest = {
  type: 'pending'
  id: string
  sessionId: string
  startedAtUtcTimestamp: Timestamp

  url: string
  method: string
  body: string | null
  startTime: DOMHighResTimeStamp
  endpoint: string

  metadata?: Record<string, unknown>
}

export type FinishedApiRequest = {
  type: 'finished'
  id: string
  sessionId: string
  startedAtUtcTimestamp: Timestamp

  url: string
  method: string
  body: string | null
  startTime: DOMHighResTimeStamp
  endpoint: string

  metadata?: Record<string, unknown>

  fetchStart: DOMHighResTimeStamp
  connectStart: DOMHighResTimeStamp
  connectEnd: DOMHighResTimeStamp
  decodedBodySize: DOMHighResTimeStamp
  domainLookupEnd: DOMHighResTimeStamp
  domainLookupStart: DOMHighResTimeStamp
  duration: DOMHighResTimeStamp
  encodedBodySize: number
  redirectStart: DOMHighResTimeStamp
  redirectEnd: DOMHighResTimeStamp
  requestStart: DOMHighResTimeStamp
  responseEnd: DOMHighResTimeStamp
  responseStart: DOMHighResTimeStamp
  responseStatus: number
  secureConnectionStart: DOMHighResTimeStamp
  contentType: string
  transferSize: number
  serverTiming: {
    description: string
    duration: DOMHighResTimeStamp
    name: string
  }[]
}

export type ApiRequest = PendingApiRequest | FinishedApiRequest

export const isPendingRequest = (req: ApiRequest): req is PendingApiRequest => {
  return req.type === 'pending'
}

export const isFinishedRequest = (req: ApiRequest): req is FinishedApiRequest => {
  return req.type === 'finished'
}

export interface RequestObservationState {
  sessionId: string
  isEnabled: boolean
  requests: ApiRequest[]
  persistRequests: boolean
}

interface RequestStoredState extends StoredState<'tms-admin.request-observer'> {
  requests: FinishedApiRequest[]
  isEnabled: boolean
  persistRequests: boolean
}

const storedVersion = 'v0.1.0' as ReduxVersion

function loadPersistedRequests(): RequestStoredState | undefined {
  const initState = localStorage.getItem('tms-admin.request-observer')
  if (!initState) {
    return undefined
  }

  try {
    const parsed = JSON.parse(initState) as RequestStoredState
    if (!isStoredStateOfType<RequestStoredState['__type']>(parsed, 'tms-admin.request-observer')) {
      console.error('Tossing request observer state, type mismatch', parsed)
      localStorage.removeItem('tms-admin.request-observer')
      return undefined
    }
    if (parsed.__version !== storedVersion) {
      console.error(`Tossing request observer state, version mismatch: Expected '${storedVersion}', received '${parsed.__version}'`, parsed)
      localStorage.removeItem('tms-admin.request-observer')
      return undefined
    }
    return parsed
  } catch (e) {
    console.error('Failed to parse persisted request observer state', e)
    localStorage.removeItem('tms-admin.request-observer')
    return undefined
  }
}

function persistState(state: RequestObservationState) {
  const toStore: RequestStoredState = {
    __type: 'tms-admin.request-observer',
    __version: storedVersion,
    __maicVersion: `v${VITE_APP_VERSION} (${VITE_COMMIT_SHA})`,
    __updatedAt: Date.now(),
    requests: state.persistRequests ? state.requests.filter(isFinishedRequest) : [],
    isEnabled: state.isEnabled,
    persistRequests: state.persistRequests,
  }
  try {
    localStorage.setItem('tms-admin.request-observer', JSON.stringify(toStore))
  } catch (e) {
    console.error('Failed to persist request observer state', e)
  }
}

const storedState = loadPersistedRequests()

const initialState: RequestObservationState = {
  sessionId: v4(),
  isEnabled: storedState?.isEnabled ?? true,
  persistRequests: storedState?.persistRequests ?? true,
  requests: storedState?.requests ?? [],
}

const maximumRequestCount = 2500
export const requestObserverSlice = createSlice({
  name: 'requestObserver',
  initialState: initialState,
  reducers: {
    setIsEnabled(state, action: { payload: boolean }) {
      state.isEnabled = action.payload
      persistState(state)
    },
    setPersistRequests(state, action: { payload: boolean }) {
      state.persistRequests = action.payload
      persistState(state)
    },
    startApiRequest(state, action: { payload: Omit<PendingApiRequest, 'sessionId'> }) {
      state.requests.unshift({
        sessionId: state.sessionId,
        ...action.payload,
      })
    },
    finishApiRequest(state, action: { payload: Omit<FinishedApiRequest, 'sessionId'> }) {
      const existingIndex = state.requests.findIndex((r) => r.id === action.payload.id)
      if (existingIndex >= 0) {
        state.requests[existingIndex] = {
          sessionId: state.sessionId,
          ...action.payload,
        }
      } else {
        state.requests.unshift({
          sessionId: state.sessionId,
          ...action.payload,
        })
      }

      if (state.requests.length > maximumRequestCount) {
        state.requests.splice(maximumRequestCount, state.requests.length - maximumRequestCount)
      }

      if (state.persistRequests) {
        persistState(state)
      }
    },
    clearRequests(state) {
      state.requests = []
      if (state.persistRequests) {
        persistState(state)
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(setIsEnabled.match, (_state, action) => {
      apiRequestPerformanceObserver.setEnabled(action.payload)
    })
  },
})

export const { setIsEnabled, setPersistRequests, finishApiRequest, clearRequests, startApiRequest } = requestObserverSlice.actions
